import { Component, h, Prop, State, Method, Element, Watch } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

export declare interface ReferencesItem {
  anchorId: string;
  label: string;
  text: string;
}

enum TransitionStatus {
  COLLAPSED = 'COLLAPSED',
  COLLAPSING = 'COLLAPSING',
  EXPANDED = 'EXPANDED',
  EXPANDING = 'EXPANDING',
}

/**
 * COMPNAME is for creating a container for references at the bottom of a journal article. Clicking on any link on the page with an href matching one of the references will expand the component and scroll the page to that reference.
 */
@Component({
  tag: 'aafp-journal-article-references',
  shadow: false,
})
export class AafpJournalArticleReferences {
  /**
   * For the heading
   */
  @Prop() heading: string;

  /**
   * Should the component begin in the expanded state?
   */
  @Prop() expandedByDefault: boolean;

  /**
   * Hides the toggle button and the heading
   */
  @Prop() hideToggleButton: boolean;

  /**
   * An array of references as JSON, each with: { anchorId: "afp20190201p152a-b2", label: "", text: "" }
   */
  @Prop() references!: string;

  @State() transitionStatus: TransitionStatus = TransitionStatus.COLLAPSED;
  @State() referencesJson: Array<ReferencesItem> = [];

  @Element() element: HTMLElement;

  private contentElement: HTMLElement;
  private currentRaf: number;

  componentWillLoad() {
    this.onReferenceLinkClick = this.onReferenceLinkClick.bind(this);
    document.addEventListener('click', this.onReferenceLinkClick, false);

    this.parseReferencesProp(this.references);
    if (this.expandedByDefault || this.hideToggleButton) this.transitionStatus = TransitionStatus.EXPANDED;
  }

  async componentDidLoad() {
    const hash = window.location.hash;

    // if a hash is present in the url when the component first renders
    // scroll to that reference
    if (hash) {
      // wait for window.load, but timeout after 3s to avoid unexpected
      // scroll jacking if page load hung, e.g. due to a slow 3rd party asset
      if ((document as any).fonts) {
        await (document as any).fonts.ready;
      }

      if (document.readyState === 'complete') {
        await this.scrollToReference(hash);
      } else {
        let didTimeout = false;
        const scrollTimeout = setTimeout(() => (didTimeout = true), 20000);

        window.addEventListener(
          'load',
          async () => {
            if (!didTimeout) {
              clearTimeout(scrollTimeout);
              await this.scrollToReference(hash);
            }
          },
          { once: true },
        );
      }
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.currentRaf);
    document.removeEventListener('click', this.onReferenceLinkClick, false);
  }

  @Watch('references')
  parseReferencesProp(newValue: string) {
    if (newValue) {
      this.referencesJson = JSON.parse(newValue);

      if (this.referencesJson.length <= 1) {
        window.cancelAnimationFrame(this.currentRaf);

        if (this.contentElement) {
          this.contentElement.style.height = '';
        }

        this.transitionStatus = TransitionStatus.EXPANDED;
      }
    }
  }

  @Method()
  async toggle(shouldAnimate?: boolean) {
    if (this.transitionStatus === TransitionStatus.EXPANDING || this.transitionStatus === TransitionStatus.EXPANDED) {
      this.collapse(shouldAnimate);
    } else {
      this.expand(shouldAnimate);
    }
  }

  @Method()
  async expand(shouldAnimate?: boolean) {
    if (shouldAnimate) {
      if (this.transitionStatus !== TransitionStatus.COLLAPSED) {
        return;
      }

      window.cancelAnimationFrame(this.currentRaf);
      this.animateExpand();
    } else {
      window.cancelAnimationFrame(this.currentRaf);
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.EXPANDED;
    }
  }

  @Method()
  async collapse(shouldAnimate?: boolean) {
    if (shouldAnimate) {
      if (this.transitionStatus !== TransitionStatus.EXPANDED) {
        return;
      }

      window.cancelAnimationFrame(this.currentRaf);
      this.animateCollapse();
    } else {
      window.cancelAnimationFrame(this.currentRaf);
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.COLLAPSED;
    }
  }

  @Method()
  async scrollToReference(href: string) {
    const matchingReference = this.referencesJson.filter((item) => `#${item.anchorId}` === href)[0];

    if (matchingReference) {
      const el: HTMLElement | null = this.element.querySelector(href);

      if (el) {
        await this.expand(false);

        // we need to wait a tick because the component has not re-rendered yet
        // after expanded was set to true
        return await this.scrollToElement(el, href);
      }
    }
  }

  private onReferenceLinkClick(e: MouseEvent) {
    const linkTarget = (e.target as HTMLAnchorElement).closest('a');

    if (!linkTarget || e.ctrlKey || e.altKey || e.shiftKey || e.metaKey || linkTarget.target === '_blank') {
      return;
    }

    const href = linkTarget.getAttribute('href');

    if (!href || href.charAt(0) !== '#') {
      return;
    }

    for (let i = 0; i < this.referencesJson.length; i++) {
      if (href === `#${this.referencesJson[i].anchorId}`) {
        e.preventDefault();
        this.scrollToReference(href);
        break;
      }
    }
  }

  private scrollToElement(el: HTMLElement | null, href: string): Promise<void> {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        if (el) {
          el.scrollIntoView(true);

          if (window.location.hash !== href) {
            window.history.pushState(null, document.title, href);
          }
        }

        resolve();
      });
    });
  }

  private animateExpand() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    if (currentHeight === 0) {
      this.transitionStatus = TransitionStatus.EXPANDED;
      return;
    }

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to expanding, applies css classes
    this.transitionStatus = TransitionStatus.EXPANDING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // measure the height the content would be when fully expanded
      const scrollHeight = this.contentElement.scrollHeight;

      // define a callback for when the component is completely expanded
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.EXPANDING) {
          return;
        }

        // update state to expanded, clear out inline style for the height
        this.transitionStatus = TransitionStatus.EXPANDED;
        this.contentElement.style.height = '';
      };

      if (currentHeight >= scrollHeight) {
        onTransitionEnd();
        return;
      }

      // set the height to its final value to kick off the css transition
      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${scrollHeight}px`;
    });
  }

  private animateCollapse() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to collapsing, applies css classes
    this.transitionStatus = TransitionStatus.COLLAPSING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // define a callback for when the component is completely collapsed
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.COLLAPSING) {
          return;
        }

        // update state to collapsed, clear out inline style for the height
        this.transitionStatus = TransitionStatus.COLLAPSED;
        this.contentElement.style.height = '';
      };

      // set height to only the height of the first item to kick off the css transition
      let targetHeight = 0;

      const firstChild: Element | null = this.contentElement.firstElementChild;

      if (firstChild) {
        targetHeight = firstChild.scrollHeight;
      }

      if (currentHeight <= targetHeight) {
        onTransitionEnd();
        return;
      }

      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${targetHeight}px`;
    });
  }

  render() {
    const disableTruncation = this.referencesJson.length <= 1;

    return (
      <div
        class={getClassListString(
          {
            'adl-journal-article-references--expanded': this.transitionStatus === TransitionStatus.EXPANDED,
            'adl-journal-article-references--expanding': this.transitionStatus === TransitionStatus.EXPANDING,
            'adl-journal-article-references--collapsed': this.transitionStatus === TransitionStatus.COLLAPSED,
            'adl-journal-article-references--collapsing': this.transitionStatus === TransitionStatus.COLLAPSING,
          },
          'adl-journal-article-references',
        )}
      >
        {!this.hideToggleButton && (
          <h2 class="adl-journal-article-references__heading" id="aafp-journal-article-references__heading">
            {disableTruncation ? (
              this.heading
            ) : (
              <button
                class="adl-journal-article-references__toggle-btn"
                onClick={() => this.toggle(true)}
                aria-controls="aafp-journal-article-references__list"
                aria-expanded={String(
                  this.transitionStatus === TransitionStatus.EXPANDED ||
                    this.transitionStatus === TransitionStatus.EXPANDING,
                )}
              >
                <span class="material-icons" aria-hidden="true">
                  expand_more
                </span>
                {this.heading}
              </button>
            )}
          </h2>
        )}
        <ol
          class="adl-journal-article-references__list adl-body"
          id="aafp-journal-article-refernces__list"
          aria-labelledby="aafp-journal-article-references__heading"
          ref={(el) => (this.contentElement = el as HTMLElement)}
        >
          {this.referencesJson.map((reference) => {
            if (!reference.anchorId || !reference.label || !reference.text) {
              return null;
            }

            return (
              <li
                key={reference.anchorId}
                class="adl-journal-article-references__list-item"
                id={reference.anchorId}
                data-label={reference.label}
                innerHTML={reference.text}
              />
            );
          })}
        </ol>
      </div>
    );
  }
}
